/**
 * E-posta adresini normalize eder (boşlukları temizler, küçük harfe çevirir)
 * @param email - Ham e-posta adresi
 * @returns Normalize edilmiş e-posta adresi
 */
export const normalizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

/**
 * E-posta adresinin geçerli olup olmadığını kontrol eder
 * @param email - Kontrol edilecek e-posta adresi
 * @returns Geçerli ise true, değilse false
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Gelişmiş e-posta validasyonu (daha katı kurallar)
 * @param email - Kontrol edilecek e-posta adresi
 * @returns Geçerli ise true, değilse false
 */
export const isValidEmailStrict = (email: string): boolean => {
  // RFC 5322 standardına daha yakın regex
  const strictEmailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  const normalizedEmail = normalizeEmail(email);
  
  // Uzunluk kontrolü (maksimum 254 karakter)
  if (normalizedEmail.length > 254) return false;
  
  // Local part (@ öncesi) maksimum 64 karakter
  const [localPart, domain] = normalizedEmail.split('@');
  if (localPart && localPart.length > 64) return false;
  
  return strictEmailRegex.test(normalizedEmail);
};

/**
 * E-posta adresindeki yaygın hataları düzeltir
 * @param email - Düzeltilecek e-posta adresi
 * @returns Düzeltilmiş e-posta adresi
 */
export const correctCommonEmailErrors = (email: string): string => {
  let corrected = normalizeEmail(email);
  
  // Yaygın domain hatalarını düzelt
  const commonDomainErrors: Record<string, string> = {
    'gmai.com': 'gmail.com',
    'gmial.com': 'gmail.com',
    'gmaill.com': 'gmail.com',
    'yahooo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
    'hotmial.com': 'hotmail.com',
    'hotmailcom': 'hotmail.com',
    'outlok.com': 'outlook.com',
    'outllok.com': 'outlook.com'
  };
  
  // Domain kısmını kontrol et ve düzelt
  const atIndex = corrected.lastIndexOf('@');
  if (atIndex !== -1) {
    const localPart = corrected.substring(0, atIndex);
    const domain = corrected.substring(atIndex + 1);
    
    if (commonDomainErrors[domain]) {
      corrected = localPart + '@' + commonDomainErrors[domain];
    }
  }
  
  return corrected;
};

/**
 * E-posta adresinin domain kısmını döndürür
 * @param email - E-posta adresi
 * @returns Domain kısmı veya boş string
 */
export const getEmailDomain = (email: string): string => {
  const normalizedEmail = normalizeEmail(email);
  const atIndex = normalizedEmail.lastIndexOf('@');
  return atIndex !== -1 ? normalizedEmail.substring(atIndex + 1) : '';
};

/**
 * E-posta adresinin local kısmını (@ öncesi) döndürür
 * @param email - E-posta adresi
 * @returns Local kısım veya boş string
 */
export const getEmailLocalPart = (email: string): string => {
  const normalizedEmail = normalizeEmail(email);
  const atIndex = normalizedEmail.lastIndexOf('@');
  return atIndex !== -1 ? normalizedEmail.substring(0, atIndex) : normalizedEmail;
};

/**
 * E-posta adresi için öneriler sunar (yaygın domain'ler için)
 * @param email - Analiz edilecek e-posta adresi
 * @returns Önerilen e-posta adresleri dizisi
 */
export const suggestEmailCorrections = (email: string): string[] => {
  const suggestions: string[] = [];
  const normalizedEmail = normalizeEmail(email);
  const atIndex = normalizedEmail.lastIndexOf('@');
  
  if (atIndex === -1) return suggestions;
  
  const localPart = normalizedEmail.substring(0, atIndex);
  const domain = normalizedEmail.substring(atIndex + 1);
  
  // Yaygın domain'ler için öneriler
  const popularDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
  
  // Mevcut domain yaygın domain'lerden biri değilse öneri sun
  if (!popularDomains.includes(domain)) {
    // Benzer domain'leri bul (basit karakter mesafesi)
    popularDomains.forEach(popularDomain => {
      if (calculateSimilarity(domain, popularDomain) > 0.6) {
        suggestions.push(localPart + '@' + popularDomain);
      }
    });
  }
  
  return suggestions;
};

/**
 * İki string arasındaki benzerlik oranını hesaplar
 * @param str1 - İlk string
 * @param str2 - İkinci string
 * @returns Benzerlik oranı (0-1 arası)
 */
function calculateSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1.0;
  
  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

/**
 * İki string arasındaki Levenshtein mesafesini hesaplar
 * @param str1 - İlk string
 * @param str2 - İkinci string
 * @returns Levenshtein mesafesi
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }
  
  return matrix[str2.length][str1.length];
} 